import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useAppMutation from 'hooks/useAppMutation';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { BaseErrorResponse } from 'models/_base';
import {
  ApplicationDnsSetting,
  CreateAgencyDnsSettingResponse,
} from 'models/CustomDomains';
import { AGENCY_DNS_SETTINGS_BASE_QUERY_KEY } from './useFetchAgencyDnsSettings';
import { postCustomDomainsUpdatedToLegacyFrame } from './customDomains.utils';

// no migration endpoint: posting a LEGACY domain migrates it, all-or-nothing
const useMigrateAgencyDnsSettings = () => {
  const { agencyUid } = useAppUser();
  const queryClient = useQueryClient();
  const { notifyError } = useNotify();
  const { t } = useTranslation();

  const { mutate: migrateDnsSettings, isLoading: isMigrating } = useAppMutation(
    async (legacySettings: ApplicationDnsSetting[]) => {
      // sequential so a failure stops before provisioning further CloudFront tenants
      return legacySettings.reduce<Promise<ApplicationDnsSetting[]>>(
        async (previous, { domain, application }) => {
          const migratedSettings = await previous;
          const response = await API.post<CreateAgencyDnsSettingResponse>(
            'api/internal/agencies/dns-settings',
            {
              agencyUid,
              domain,
              application,
            },
          );

          return [...migratedSettings, response.data.dnsSettings];
        },
        Promise.resolve([]),
      );
    },
    {
      onSuccess: (migratedSettings) => {
        const updatedSettings = queryClient.setQueryData<
          ApplicationDnsSetting[]
        >(
          [AGENCY_DNS_SETTINGS_BASE_QUERY_KEY, agencyUid],
          (currentSettings = []) => [
            ...currentSettings.filter(
              ({ application }) =>
                !migratedSettings.some(
                  (migrated) => migrated.application === application,
                ),
            ),
            ...migratedSettings,
          ],
        );

        // clears the jsp migration banner and flips the button
        postCustomDomainsUpdatedToLegacyFrame(
          updatedSettings ?? migratedSettings,
        );
      },
      onError: (error: BaseErrorResponse) => {
        // no rollback endpoint, so refetch to show whatever partially migrated
        queryClient.invalidateQueries([
          AGENCY_DNS_SETTINGS_BASE_QUERY_KEY,
          agencyUid,
        ]);

        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage:
              error.response?.data?.apiErrorMessage ??
              t('componentCustomDomains.validateError'),
            baseKey: 'componentCustomDomains.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return { migrateDnsSettings, isMigrating };
};

export default useMigrateAgencyDnsSettings;
